import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getApiBase } from '../../utils/apiBase';
import { cn } from '@/lib/utils';

const API = getApiBase();

const REACTIONS = [
  { key: 'candle', emoji: '🕯️', label: 'Light a candle' },
  { key: 'flower', emoji: '🌹', label: 'Leave a flower' },
  { key: 'love', emoji: '❤️', label: 'Send love' },
  { key: 'dove', emoji: '🕊️', label: 'Peace' },
  { key: 'prayer', emoji: '🙏', label: 'Thinking of you' },
];

const DEFAULT_COUNTS = {
  candle: 0,
  flower: 0,
  love: 0,
  dove: 0,
  prayer: 0,
};

function storageKey(slug) {
  return `mlb-memorial-reaction-${slug || 'unknown'}`;
}

export function MemorialReactions({
  slug,
  initial = {},
  showCounts = true,
  className,
  candleStyle = 'classic',
}) {
  const [counts, setCounts] = useState({ ...DEFAULT_COUNTS, ...initial });
  const [busy, setBusy] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  const recentReaction = useMemo(() => {
    if (typeof window === 'undefined' || !slug) return null;
    try {
      return window.localStorage.getItem(storageKey(slug));
    } catch {
      return null;
    }
  }, [slug]);

  const react = useCallback(async (reaction) => {
    if (!slug || busy) return;
    if (recentReaction === reaction) {
      toast.message('You already left this quiet remembrance recently.');
      return;
    }
    setBusy(reaction);
    try {
      const { data } = await axios.post(`${API}/public/memorial/${slug}/reactions`, { reaction });
      if (data.reactions) {
        setCounts({ ...DEFAULT_COUNTS, ...data.reactions });
      }
      try {
        window.localStorage.setItem(storageKey(slug), reaction);
      } catch {
        /* ignore */
      }
      setConfirmed(reaction);
      toast.success(data.message || 'Thank you for leaving a quiet remembrance.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send reaction.');
    } finally {
      setBusy(null);
    }
  }, [slug, busy, recentReaction]);

  return (
    <div className={cn('text-center', className)} data-testid="memorial-reactions">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(32_10%_42%)]">
        Quiet remembrance
      </p>
      <p className="mx-auto mt-2 max-w-md text-sm text-[hsl(32_10%_42%)]">
        For visitors who prefer a simple gesture instead of writing a full message.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-3">
        {REACTIONS.map(({ key, emoji, label }) => (
          <Button
            key={key}
            type="button"
            variant="outline"
            size="sm"
            className="h-auto min-w-[7rem] flex-col gap-1 rounded-2xl border-[var(--memorial-gold,#c9a961)]/25 px-3 py-3 hover:border-[var(--memorial-gold,#c9a961)]/45"
            disabled={Boolean(busy) && busy !== key}
            onClick={() => react(key)}
          >
            <span className="text-lg" aria-hidden style={key === 'candle' ? { filter: 'none' } : undefined}>
              {emoji}
            </span>
            <span className="text-[11px] leading-tight">{label}</span>
            {showCounts && counts[key] > 0 ? (
              <span className="text-[10px] tabular-nums text-muted-foreground">{counts[key]}</span>
            ) : null}
          </Button>
        ))}
      </div>
      {confirmed ? (
        <p className="mt-4 text-sm text-[hsl(32_10%_42%)]">Thank you for leaving a quiet remembrance.</p>
      ) : null}
    </div>
  );
}

export default MemorialReactions;
